import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  directory: { items: [], total: 0, page: 1, q: '', loading: false, error: null, meta: null },
  attention: { items: [], total: 0, page: 1, loading: false, error: null, meta: null },
  // Pool-wide caseload header.
  health: { data: null, loading: false, error: null },
  // Quality review list, with the filters the toolbar drives.
  review: {
    items: [],
    total: 0,
    page: 1,
    loading: false,
    error: null,
    filters: { q: '', filter: 'all' },
  },
  // One thread and its transcript, opened beside the list.
  thread: { id: null, page: 1, data: null, messages: [], total: 0, loading: false, error: null },
  deletingId: null,
  deleteError: null,
};

const coachConsoleSlice = createSlice({
  name: 'coachConsole',
  initialState,
  reducers: {
    getCoachDirectoryAction: (state, action) => {
      const target = state[action.payload?.attention ? 'attention' : 'directory'];
      target.loading = true;
      target.error = null;
      target.items = [];
      target.page = action.payload?.page || 1;
      if (!action.payload?.attention) target.q = action.payload?.q || '';
    },
    getCoachDirectorySuccess: (state, action) => {
      const { attention, data, total, page, ...meta } = action.payload;
      const target = state[attention ? 'attention' : 'directory'];
      target.items = data;
      target.total = total;
      target.page = page;
      target.meta = meta;
      target.loading = false;
    },
    getCoachDirectoryFailure: (state, action) => {
      const target = state[action.payload.attention ? 'attention' : 'directory'];
      target.loading = false;
      target.error = action.payload.error;
    },
    getCaseloadHealthAction: (state) => {
      state.health.loading = true;
      state.health.error = null;
    },
    getCaseloadHealthSuccess: (state, action) => {
      state.health.data = action.payload;
      state.health.loading = false;
    },
    getCaseloadHealthFailure: (state, action) => {
      state.health.loading = false;
      state.health.error = action.payload;
    },

    setReviewFilters: (state, action) => {
      state.review.filters = { ...state.review.filters, ...action.payload };
      state.review.page = 1;
      state.review.loading = true;
      state.review.error = null;
      state.review.items = [];
    },
    setReviewPage: (state, action) => {
      state.review.page = action.payload;
      state.review.loading = true;
      state.review.error = null;
      state.review.items = [];
    },
    getReviewConversationsAction: (state) => {
      state.review.loading = true;
      state.review.error = null;
    },
    getReviewConversationsSuccess: (state, action) => {
      state.review.items = action.payload.items;
      state.review.total = action.payload.total;
      state.review.loading = false;
    },
    getReviewConversationsFailure: (state, action) => {
      state.review.items = [];
      state.review.loading = false;
      state.review.error = action.payload;
    },

    openReviewThreadAction: (state, action) => {
      // Clear the old transcript first, or the previous thread's messages show under
      // the new row's header while the request is in flight.
      state.thread = {
        id: action.payload,
        page: 1,
        data: null,
        messages: [],
        total: 0,
        loading: Boolean(action.payload),
        error: null,
      };
    },
    setReviewThreadPage: (state, action) => {
      state.thread.page = action.payload;
      state.thread.messages = [];
      state.thread.loading = true;
      state.thread.error = null;
    },
    openReviewThreadSuccess: (state, action) => {
      if (action.payload.id !== state.thread.id || action.payload.page !== state.thread.page)
        return;
      state.thread.data = action.payload.conversation;
      state.thread.messages = action.payload.messages;
      state.thread.total = action.payload.total;
      state.thread.loading = false;
    },
    openReviewThreadFailure: (state, action) => {
      if (action.payload.id !== state.thread.id || action.payload.page !== state.thread.page)
        return;
      state.thread.loading = false;
      state.thread.error = action.payload.error;
    },

    deleteReviewConversationAction: (state, action) => {
      state.deletingId = action.payload;
      state.deleteError = null;
    },
    deleteReviewConversationSuccess: (state, action) => {
      state.review.items = state.review.items.filter((row) => row.id !== action.payload);
      state.deletingId = null;
      state.review.total = Math.max(0, state.review.total - 1);
    },
    deleteReviewConversationFailure: (state, action) => {
      state.deletingId = null;
      state.deleteError = action.payload;
    },
  },
});

export const {
  getCoachDirectoryAction,
  getCoachDirectorySuccess,
  getCoachDirectoryFailure,
  setReviewThreadPage,
  setReviewPage,
  deleteReviewConversationAction,
  deleteReviewConversationSuccess,
  deleteReviewConversationFailure,
  setReviewFilters,
  openReviewThreadAction,
  openReviewThreadSuccess,
  openReviewThreadFailure,
  getCaseloadHealthAction,
  getCaseloadHealthSuccess,
  getCaseloadHealthFailure,
  getReviewConversationsAction,
  getReviewConversationsSuccess,
  getReviewConversationsFailure,
} = coachConsoleSlice.actions;

export { coachConsoleSlice };
